import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tooltip,
} from '@mui/material';
import {
  PostAdd as PostAddIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Description as DescriptionIcon,
  VpnKey as KeyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PlatformOutcomes } from '../components/PlatformOutcomes';

const API_URL = process.env.REACT_APP_API_URL || '';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [summary, setSummary] = useState<{
    totals: { engagement: number };
    publishing: { total: number; published: number; scheduled: number };
  } | null>(null);
  const [recent, setRecent] = useState<{
    id: string;
    content: string;
    status: string;
    created_at: string;
    platforms?: string[];
    results?: { platform: string; status: string; error_message?: string; platform_post_id?: string; permalink?: string | null }[];
  }[]>([]);

  // These tiles were hardcoded zeros; they now reflect real counts
  useEffect(() => {
    fetch(`${API_URL}/api/analytics`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(setSummary)
      .catch(() => setSummary(null));

    // Ask for five rather than fetching every post and slicing client-side: each post
    // costs a results query server-side, so this scales with the team's history.
    fetch(`${API_URL}/api/posts?limit=5`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : []))
      .then(setRecent)
      .catch(() => setRecent([]));
  }, [token]);

  // Each tile links to the screen that explains the number, so the figure is a starting
  // point rather than a dead end. Post History reads ?status= from the URL.
  const stats = [
    {
      title: 'Total Posts',
      value: (summary?.publishing.total ?? 0).toLocaleString(),
      icon: <PostAddIcon />,
      color: '#1976d2',
      to: '/history',
      hint: 'See every post and what happened on each platform',
    },
    {
      title: 'Scheduled',
      value: (summary?.publishing.scheduled ?? 0).toLocaleString(),
      icon: <ScheduleIcon />,
      color: '#f59e0b',
      to: '/calendar',
      hint: 'See what is queued, on the calendar',
    },
    {
      title: 'Published',
      value: (summary?.publishing.published ?? 0).toLocaleString(),
      icon: <CheckCircleIcon />,
      color: '#10b981',
      to: '/history?status=published',
      hint: 'See the posts that went out',
    },
    {
      title: 'Engagement',
      value: (summary?.totals.engagement ?? 0).toLocaleString(),
      icon: <TrendingUpIcon />,
      color: '#9c27b0',
      to: '/analytics',
      hint: 'Break engagement down by platform and over time',
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="h1">
          Dashboard
        </Typography>
        <Button
          variant="contained"
          startIcon={<PostAddIcon />}
          onClick={() => navigate('/create')}
        >
          Create Post
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
        {stats.map((stat) => (
          <Box key={stat.title}>
            <Tooltip title={stat.hint} arrow>
              <Card
                onClick={() => navigate(stat.to)}
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'box-shadow 120ms, transform 120ms',
                  '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
                }}
              >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      backgroundColor: `${stat.color}20`,
                      color: stat.color,
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      mr: 2,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Typography variant="h4" component="div">
                    {stat.value}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {stat.title}
                </Typography>
              </CardContent>
              </Card>
            </Tooltip>
          </Box>
        ))}
      </Box>

      {/* minmax(0, …) rather than bare fr: an fr track has an auto minimum, so long
          unbroken content in the left card could still stretch the grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr)' }, gap: 3, mt: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              {recent.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No recent activity yet. Create your first post to get started!
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                  {recent.map((post, index) => (
                    <Box
                      key={post.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                        alignItems: 'flex-start',
                        py: 1.5,
                        borderBottom: index < recent.length - 1 ? 1 : 0,
                        borderColor: 'divider',
                      }}
                    >
                      {/* minWidth: 0 is what actually lets this shrink. Without it the
                          nowrap text keeps its natural width and stretches the card. */}
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {post.content}
                        </Typography>

                        {/* The same component Post History uses, rather than a second copy
                            of the same chip: the dashboard's had drifted, losing the status
                            icon and showing a rejected platform in red as though the network
                            had refused it. */}
                        <Box sx={{ mt: 0.75 }}>
                          <PlatformOutcomes
                            platforms={post.platforms || []}
                            results={post.results}
                            dense
                          />
                        </Box>
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{
                          textTransform: 'capitalize',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          color: post.status === 'published' ? 'success.main'
                            : post.status === 'failed' ? 'error.main'
                            : 'text.secondary',
                        }}
                      >
                        {post.status}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/create')}
                >
                  Create New Post
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/connections')}
                >
                  Connect Platforms
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/scheduled')}
                >
                  View Scheduled
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Not for the LinkedIn reviewer: it cannot create keys, and the API is not what it is reviewing */}
          {user?.role !== 'demo' && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Developer API
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Publish and schedule posts from your own scripts and applications.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<DescriptionIcon />}
                  onClick={() => navigate('/api-documentation')}
                >
                  API Documentation
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<KeyIcon />}
                  onClick={() => navigate('/settings')}
                >
                  Manage API Keys
                </Button>
              </Box>
            </CardContent>
          </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
};
